import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, ZoomControl, useMap } from "react-leaflet";
import type { Map as LeafletMap } from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  GOOGLE_MAPS_API_KEY,
  OSM_ATTRIBUTION,
  OSM_URL,
  fetchGoogleCopyright,
  getGoogleSession,
  googleTileUrl,
  type GoogleMapMode,
  type GoogleSession,
} from "@/lib/googleTiles";

// "osm" continua aceito por compatibilidade; os demais sao os modos Google.
export type BaseLayerMode = GoogleMapMode | "osm";

export interface FloraIQMapProps {
  center?: [number, number];
  zoom?: number;
  minZoom?: number;
  maxZoom?: number;
  defaultBaseLayer?: BaseLayerMode;
  /** Mantido por compatibilidade; o seletor de mapa-base e sempre exibido. */
  showLayerControl?: boolean;
  showZoomControl?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  onMapReady?: (map: LeafletMap) => void;
  interactive?: boolean;
}

const MODES: { id: GoogleMapMode; label: string }[] = [
  { id: "hybrid", label: "Híbrido" },
  { id: "satellite", label: "Satélite" },
  { id: "roadmap", label: "Ruas" },
  { id: "roadmap_clean", label: "Sem rótulos" },
];

const MODE_KEY = "floraiq_web_basemap";

function MapReadyHandler({ onMapReady }: { onMapReady?: (map: LeafletMap) => void }) {
  const map = useMap();
  const called = useRef(false);
  useEffect(() => {
    if (onMapReady && !called.current) {
      called.current = true;
      onMapReady(map);
    }
  }, [map, onMapReady]);
  return null;
}

// Copyright da area visivel, exigido pelos termos da Map Tiles API.
function GoogleAttribution({ session }: { session: GoogleSession }) {
  const map = useMap();
  useEffect(() => {
    let current = "";
    let timer: ReturnType<typeof setTimeout> | undefined;
    const update = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const b = map.getBounds();
        const copyright = await fetchGoogleCopyright(session, map.getZoom(), {
          north: b.getNorth(),
          south: b.getSouth(),
          east: b.getEast(),
          west: b.getWest(),
        });
        const text = `Google${copyright ? ` · ${copyright}` : ""}`;
        if (current) map.attributionControl.removeAttribution(current);
        map.attributionControl.addAttribution(text);
        current = text;
      }, 400);
    };
    update();
    map.on("moveend", update);
    return () => {
      clearTimeout(timer);
      map.off("moveend", update);
      if (current) map.attributionControl.removeAttribution(current);
    };
  }, [map, session]);
  return null;
}

function BaseLayerToggle({
  mode,
  googleOk,
  onChange,
}: {
  mode: BaseLayerMode;
  googleOk: boolean;
  onChange: (m: GoogleMapMode) => void;
}) {
  return (
    <div className="leaflet-top leaflet-right" style={{ pointerEvents: "auto" }}>
      <div
        className="leaflet-control bg-white rounded-lg shadow-md border border-gray-200 p-1 flex gap-0.5"
        style={{ marginTop: 10, marginRight: 10 }}
      >
        {MODES.map((m) => {
          // Sem Google, so "Ruas" funciona (via OpenStreetMap).
          const disabled = !googleOk && m.id !== "roadmap";
          const active = mode === m.id || (mode === "osm" && m.id === "roadmap");
          return (
            <button
              key={m.id}
              onClick={() => onChange(m.id)}
              disabled={disabled}
              title={disabled ? "Requer a Map Tiles API do Google" : undefined}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                active ? "bg-primary-600 text-white" : "text-gray-600 hover:bg-gray-100"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DEFAULT_CENTER: [number, number] = [-16.7145, -49.262];
const DEFAULT_ZOOM = 15;

function initialMode(fallback: BaseLayerMode): BaseLayerMode {
  try {
    const saved = localStorage.getItem(MODE_KEY) as BaseLayerMode | null;
    if (saved && (saved === "osm" || MODES.some((m) => m.id === saved))) return saved;
  } catch {
    /* sem preferencia salva */
  }
  return fallback;
}

export default function FloraIQMap({
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  minZoom = 3,
  maxZoom = 21,
  defaultBaseLayer = "hybrid",
  showZoomControl = true,
  className = "",
  style,
  children,
  onMapReady,
  interactive = true,
}: FloraIQMapProps) {
  const [mode, setMode] = useState<BaseLayerMode>(() => initialMode(defaultBaseLayer));
  const [session, setSession] = useState<GoogleSession | null>(null);
  const [googleOk, setGoogleOk] = useState(Boolean(GOOGLE_MAPS_API_KEY));

  useEffect(() => {
    let cancelled = false;
    setSession(null);
    if (mode === "osm" || !googleOk) return;
    getGoogleSession(mode)
      .then((s) => !cancelled && setSession(s))
      .catch(() => {
        if (cancelled) return;
        // API desativada ou chave recusada: fica no OpenStreetMap.
        setGoogleOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, googleOk]);

  const changeMode = (m: GoogleMapMode) => {
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* sem preferencia salva */
    }
  };

  const useGoogle = googleOk && mode !== "osm" && session !== null;

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      zoomControl={false}
      scrollWheelZoom={interactive}
      dragging={interactive}
      doubleClickZoom={interactive}
      touchZoom={interactive}
      boxZoom={interactive}
      keyboard={interactive}
      className={`w-full h-full ${className}`}
      style={style}
    >
      {useGoogle ? (
        <>
          {/* Tiles de 512 px (highDpi) desenhados em 256: nitidez em telas de alta densidade. */}
          <TileLayer key={`g-${mode}-${session.session}`} url={googleTileUrl(session)} tileSize={256} maxZoom={maxZoom} />
          <GoogleAttribution session={session} />
        </>
      ) : (
        <TileLayer key="osm" url={OSM_URL} attribution={OSM_ATTRIBUTION} maxNativeZoom={19} maxZoom={maxZoom} />
      )}

      {showZoomControl && <ZoomControl position="bottomright" />}

      <BaseLayerToggle mode={googleOk ? mode : "osm"} googleOk={googleOk} onChange={changeMode} />

      <MapReadyHandler onMapReady={onMapReady} />

      {children}
    </MapContainer>
  );
}

export { DEFAULT_CENTER, DEFAULT_ZOOM };
